import React, { useState, useEffect, useMemo } from "react";
import useFileManagerApi from "../../hooks/api/useFileManagerApi";

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', 'wmv', 'flv'];
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'];

const ImagePreviewModal = ({ isOpen, onClose, image }) => {
  const [imageError, setImageError] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { fetchFolderStructure, requestDownloadLink } = useFileManagerApi();

  const extension = useMemo(
    () => image?.name?.split('.').pop()?.toLowerCase() || "",
    [image?.name]
  );

  const previewCategory = image?.previewCategory || null;
  const isVideo = useMemo(
    () => previewCategory === 'video' || VIDEO_EXTENSIONS.includes(extension),
    [previewCategory, extension]
  );
  const isDocument = useMemo(
    () => previewCategory === 'document' || DOCUMENT_EXTENSIONS.includes(extension),
    [previewCategory, extension]
  );

  // Helper function to find file in structure by path
  const findFileInStructure = (structure, filePath) => {
    if (!structure || !filePath) return null;
    
    const pathParts = filePath.split('/').filter(Boolean);
    let current = structure;
    
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      
      // Skip metadata properties
      if (part === 'isFolder' || part === 'hasFiles' || part === 'path' || 
          part === 'lastModified' || part === 'size' || part === 'etag') {
        continue;
      }
      
      if (current[part]) {
        const item = current[part];
        
        // If this is the last part
        if (i === pathParts.length - 1) {
          // Check if it's a file (not a folder)
          if (item.isFolder === false || (typeof item === 'object' && !item.isFolder)) {
            return {
              name: part,
              size: item.size || 0,
              lastModified: item.lastModified || "",
              type: part.split('.').pop()?.toUpperCase() || 'UNKNOWN'
            };
          }
        } else {
          // Continue traversing
          current = item;
        }
      } else {
        return null;
      }
    }
    return null;
  };

  // Format date: luna, zi, an, ora
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${month} ${day} ${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb * 100) / 100} MB`;
  };

  useEffect(() => {
    // Reset states when modal opens/closes or image changes
    if (!isOpen || !image) {
      setImageError(false);
      setImageData(null);
      setImageUrl(null);
      return;
    }

    const fetchImageData = async () => {
      setLoading(true);
      setImageError(false);
      
      try {
        // Get folder structure
        const directoryStructure = await fetchFolderStructure();
        
        // Build file path from image data
        // image.id should contain the full path, or we can construct it from folderPath and name
        let filePath =
          image.previewPath ||
          image.id ||
          (image.folderPath ? `${image.folderPath}/${image.name}` : image.name);
        
        // Remove leading slash if present for structure lookup
        const pathForLookup = filePath.replace(/^\/+/, '');
        
        // Find file in structure
        const fileData = findFileInStructure(directoryStructure, pathForLookup);
        
        if (fileData) {
          setImageData({
            type: fileData.type,
            date: formatDate(fileData.lastModified),
            size: formatFileSize(fileData.size),
            rawSize: fileData.size
          });
        } else {
          // Fallback to image props if not found in structure
          const extension = image.name.split('.').pop()?.toUpperCase() || 'UNKNOWN';
          setImageData({
            type: extension,
            date: formatDate(image.date),
            size: formatFileSize(image.size || 0),
            rawSize: image.size || 0
          });
        }
        
        // Get presigned URL for image
        let normalizedPath = filePath.startsWith("/") ? filePath : `/${filePath}`;
        normalizedPath = normalizedPath.replace(/\/+$/, ""); // Remove trailing slash for files
        
        const downloadResponse = await requestDownloadLink(normalizedPath);
        
        if (downloadResponse.data.success && downloadResponse.data.presignedUrl) {
          setImageUrl(downloadResponse.data.presignedUrl);
        } else {
          setImageError(true);
        }
      } catch (error) {
        console.error("Error fetching image data:", error);
        setImageError(true);
        // Fallback to image props
        const extension = image.name.split('.').pop()?.toUpperCase() || 'UNKNOWN';
        setImageData({
          type: extension,
          date: formatDate(image.date),
          size: formatFileSize(image.size || 0),
          rawSize: image.size || 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchImageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, image?.id, image?.name, image?.folderPath]);

  if (!isOpen || !image) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col min-h-[374px] max-h-[90vh] w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 mobile:p-2 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-2">
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.65 0H1.35005C0.605059 0 0 0.603652 0 1.34691V12.5528V12.6511C0 12.9008 0.0689806 13.1348 0.187233 13.3354C0.421767 13.7326 0.85536 14 1.35005 14H16.65C17.1446 14 17.5782 13.7326 17.8128 13.3354C17.9212 13.1525 17.9882 12.9402 17.998 12.716C18 12.6944 18 12.6728 18 12.6531V1.34888C18 0.603651 17.393 0 16.65 0ZM12.9861 7.46601C12.657 7.12191 12.1031 7.10225 11.7504 7.42275L9.80313 9.20028L6.77784 5.40534C6.60637 5.19101 6.35016 5.0691 6.07621 5.07303C5.80029 5.07697 5.54801 5.20477 5.38246 5.42303L0.817913 11.4674V1.34691C0.817913 1.0559 1.05639 0.817977 1.34808 0.817977H16.65C16.9416 0.817977 17.1801 1.0559 17.1801 1.34691V11.8567L12.9861 7.46601Z" fill="#4B5563"/>
            <path d="M13.5 3C12.6736 3 12 3.67359 12 4.5C12 5.32641 12.6736 6 13.5 6C14.3264 6 15 5.32641 15 4.5C14.998 3.67158 14.3264 3 13.5 3Z" fill="#4B5563"/>
          </svg>
          <span className="text-bodyMR text-dark4 font-medium mobile:text-bodyESM">
            {image.name ? image.name.split('.').slice(0, -1).join('.') || image.name : image.name}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-dark4 hover:text-red-600 transition-colors p-1"
          aria-label="Close modal"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Image Preview */}
      <div className="flex-1 flex items-center justify-center mobile:p-2 p-6 bg-gray-50 overflow-auto">
        <div className="w-full h-full flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 mb-2"></div>
              <span className="text-sm text-gray-500">Loading preview...</span>
            </div>
          ) : imageError ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mb-2"
              >
                <path
                  d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
              </svg>
              <span className="text-sm text-gray-500">Preview not available</span>
            </div>
          ) : imageUrl ? (
            isVideo ? (
              <video
                controls
                src={imageUrl}
                className="max-w-full max-h-[320px] rounded-lg bg-black shadow-lg"
                onError={() => setImageError(true)}
              >
                Your browser does not support video playback.
              </video>
            ) : isDocument ? (
              <iframe
                title={image.name}
                src={imageUrl}
                className="w-full h-[320px] rounded-lg bg-white shadow-inner"
                frameBorder="0"
                onError={() => setImageError(true)}
              />
            ) : (
              <img
                src={imageUrl}
                alt={image.name}
                className="max-w-full max-h-[320px] object-contain rounded-lg shadow-lg"
                onError={() => {
                  setImageError(true);
                }}
              />
            )
          ) : null}
        </div>
      </div>

      {/* Footer Info */}
      <div className="px-4 py-2 mobile:p-2 border-t border-gray-200 bg-white">
        <div className="space-y-2">
          <div className="flex text-bodySR mobile:text-bodyESM">
            <span className="text-gray-600">Type:</span>
            <span className="text-dark4 font-medium ml-2">{imageData?.type || '-'}</span>
          </div>
          <div className="flex text-bodySR mobile:text-bodyESM">
            <span className="text-gray-600">Date:</span>
            <span className="text-dark4 ml-2">{imageData?.date || '-'}</span>
          </div>
          <div className="flex text-bodySR mobile:text-bodyESM">
            <span className="text-gray-600">Size:</span>
            <span className="text-dark4 ml-2">{imageData?.size || '-'}</span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
